import { isNode, parse, parseDocument, visit } from "yaml";

import type { CandidateSummary } from "@/api/preview";
import type { LabTurns } from "@/lib/labModel";
import { isAttested } from "@/lib/previewInsights";
import type {
  ClassificationEvent,
  ConfigIdentity,
  CriterionEvent,
  RoutingEvent,
  RuntimePosition,
  TraceEvent,
  WhyNotEvent,
} from "@/lib/previewDecisions";

/** Every value is the producer's (events, the answers body, the run's effective documents); one none of them carries stays `null`, never a guess. */

/** step id -> track -> weight, as `main_concern_diagnosis.yaml` states it. */
export type MainConcernWeights = Readonly<
  Record<string, Readonly<Record<string, number>>>
>;

export type StepTexts = Readonly<Record<string, string>>;

/** What a round's points and statements are read from. */
export type MainConcernDocuments = {
  weights: MainConcernWeights | null;
  stepTexts: StepTexts | null;
};

/** The main-concern result body: step id -> whether the classifier answered yes. */
export type MainConcernAnswers = Readonly<Record<string, boolean>>;

/** `main_concern.next_track`: the routing decision that closes a main-concern round. */
const NEXT_TRACK_SUFFIX = ".next_track";
const VERDICT_CRITERION = "diagnosis";

export type StepState = "yes" | "no" | "unknown";

export type TrackStep = {
  step: string;
  text: string | null;
  state: StepState;
  /** The weight the document gives this step for the track, signed; null without the document. */
  points: number | null;
};

export type TrackScore = {
  track: string;
  selected: boolean;
  /** The weighted condition count; null when neither a why-not nor the answers carry it. */
  score: number | null;
  /** The track's attainable total (its positive weights); null without the weight document. */
  max: number | null;
  steps: readonly TrackStep[];
};

export type MainConcernRound = {
  round: number;
  turnNumber: number | null;
  decisionEventRef: string;
  selectedTrack: string;
  /** Highest score first; the selected track leads its ties. */
  tracks: readonly TrackScore[];
  /** The classification event whose body holds the answers (to read on request). */
  classificationEventRef: string | null;
  /** The weight document the decision cites; its points and step texts live beside it. */
  weightsDocument: ConfigIdentity | null;
};

export type EvaluationCriterion = {
  criterionId: string;
  label: string;
  passed: boolean;
};

/** One sub-step of a screening or full evaluation: the classifier's yes/no and the reasoning behind it. */
export type SubStep = {
  id: string;
  passed: boolean;
  /** The model's own words (label and quoted patient words); null when no rationale was recorded. */
  reasoning: string | null;
};

export type StepGroup = { step: string; subSteps: readonly SubStep[] };

/** What a classification event's result (and rationale) body says, step by step. */
export type ClassifierReading = {
  groups: readonly StepGroup[];
  hasReasoning: boolean;
};

export type FlowStatus = "done" | "current" | "next";

/** One stop on the conversation's route, as far as the events show it. */
export type FlowStep = { id: string; label: string; status: FlowStatus };

export type FinishedEvaluation = {
  classifierId: string;
  /** The classification event whose body holds the sub-steps. */
  classificationEventRef: string | null;
  /** The sub-steps read from that body; null until read, or when it is withheld or not a step map. */
  reading: ClassifierReading | null;
  turnNumber: number | null;
  /** The `<id>.diagnosis` verdict; null when the evaluation recorded none. */
  passed: boolean | null;
  /** A result the runtime computed and set aside (a fan-out unit a diagnosed sibling displaced). */
  setAside: boolean;
  criteria: readonly EvaluationCriterion[];
};

export type ClassificationState = {
  position: RuntimePosition | null;
  rounds: readonly MainConcernRound[];
  evaluations: readonly FinishedEvaluation[];
  /** Main concern, the evaluations that ran, the current stage and a selected track not yet started. */
  flow: readonly FlowStep[];
  /** Nothing classified yet: no round and no finished evaluation. */
  empty: boolean;
};

export type ClassificationStateInput = {
  lab: LabTurns;
  /** The selected turn; null reads the whole run. */
  throughTurn: number | null;
  /** The effective documents, by the weight document's config id a round cites. */
  documents?: ReadonlyMap<string, MainConcernDocuments>;
  /** Main-concern answers by the classification event's `eventRef`. */
  answers?: ReadonlyMap<string, MainConcernAnswers>;
  /** Sub-step readings by the classification event's `eventRef` (screenings and full evaluations). */
  readings?: ReadonlyMap<string, ClassifierReading>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Natural step order: step2 before step10. */
function stepOrder(a: string, b: string): number {
  return a.localeCompare(b, "en", { numeric: true });
}

function weightsOf(doc: unknown): MainConcernWeights | null {
  if (!isRecord(doc)) {
    return null;
  }
  const weights: Record<string, Record<string, number>> = {};
  for (const [step, tracks] of Object.entries(doc)) {
    if (!isRecord(tracks)) {
      return null;
    }
    const row: Record<string, number> = {};
    for (const [track, weight] of Object.entries(tracks)) {
      if (typeof weight !== "number" || !Number.isFinite(weight)) {
        return null;
      }
      row[track] = weight;
    }
    weights[step] = row;
  }
  return weights;
}

/** The weight document, or null when the text is not a step -> track -> number map. */
export function parseMainConcernWeights(
  text: string,
): MainConcernWeights | null {
  let doc: unknown;
  try {
    doc = parse(text);
  } catch {
    return null;
  }
  return weightsOf(doc);
}

/** A parsed YAML value, or null when it does not parse or carries a `!delete` tombstone. */
function readWithoutTombstones(text: string): unknown {
  const doc = parseDocument(text);
  if (doc.errors.length > 0) {
    return null;
  }
  let tombstone = false;
  visit(doc, (_key, node) => {
    if (isNode(node) && node.tag === "!delete") {
      tombstone = true;
      return visit.BREAK;
    }
    return undefined;
  });
  if (tombstone) {
    return null;
  }
  try {
    return doc.toJS();
  } catch {
    // Valid YAML can still exceed the library's alias limit; that is unreadable, not a crash.
    return null;
  }
}

export type WeightsReading = {
  weights: MainConcernWeights | null;
  /** `missing`: neither layer has the file; `unreadable`: a layer does not parse, has a `!delete`, or is not a weight map. */
  problem: "missing" | "unreadable" | null;
};

/**
 * The weight document the run used: the candidate's file merged over the base
 * pack's, ONE level deep per step key (`screening_semantics.merge_inherited`).
 * `!delete` tombstones are not honoured by the service for this document, so a
 * file carrying one is reported unreadable rather than guessed at.
 */
export function effectiveWeights(
  overlay: string | null,
  base: string | null,
): WeightsReading {
  if (overlay === null && base === null) {
    return { weights: null, problem: "missing" };
  }
  const layers = [base, overlay].map((text) =>
    text === null ? {} : readWithoutTombstones(text),
  );
  const [inherited, authored] = layers;
  if (!isRecord(inherited) || !isRecord(authored)) {
    return { weights: null, problem: "unreadable" };
  }
  const merged: Record<string, unknown> = { ...inherited };
  for (const [step, value] of Object.entries(authored)) {
    const held = merged[step];
    merged[step] =
      isRecord(held) && isRecord(value) ? { ...held, ...value } : value;
  }
  const weights = weightsOf(merged);
  return weights === null
    ? { weights: null, problem: "unreadable" }
    : { weights, problem: null };
}

const STATEMENT_HEADING = /^## Step (\d+): (.+)$/gm;

/** `main_concern_classification.txt`: the `## Step N: <statement>` headings as `stepN -> statement`. */
export function parseClassifierStatements(text: string): StepTexts {
  const statements: Record<string, string> = {};
  for (const match of text.matchAll(STATEMENT_HEADING)) {
    statements[`step${match[1]}`] = (match[2] ?? "").trim();
  }
  return statements;
}

/** The statements the run used: the candidate's file replaces the base's whole. */
export function effectiveStatements(
  overlay: string | null,
  base: string | null,
): StepTexts | null {
  const text = overlay ?? base;
  return text === null ? null : parseClassifierStatements(text);
}

/** The result body as the producer records it: `{ step1: true, step2: false, ... }`. */
export function parseMainConcernAnswers(
  inline: string,
): MainConcernAnswers | null {
  let body: unknown;
  try {
    body = JSON.parse(inline);
  } catch {
    return null;
  }
  if (!isRecord(body)) {
    return null;
  }
  const answers: Record<string, boolean> = {};
  for (const [step, value] of Object.entries(body)) {
    if (typeof value === "boolean") {
      answers[step] = value;
    }
  }
  return answers;
}

function readJsonRecord(text: string): Record<string, unknown> | null {
  try {
    const body: unknown = JSON.parse(text);
    return isRecord(body) ? body : null;
  } catch {
    return null;
  }
}

/**
 * A screening or full-evaluation result body (`{ step1: { step_1_1: true } }`),
 * with the rationale body beside it (`{ step1: { step_1_1: { value, reasoning } } }`)
 * when one was recorded. A flat `{ step1: true }` answer reads as a step of its own.
 * Null when the result is not a step map; a rationale that does not read only
 * costs the reasoning.
 */
export function parseClassifierReading(
  result: string,
  rationale: string | null,
): ClassifierReading | null {
  const body = readJsonRecord(result);
  if (body === null) {
    return null;
  }
  const why = rationale === null ? null : readJsonRecord(rationale);
  const reasoningOf = (step: string, sub: string): string | null => {
    const leaf = isRecord(why?.[step]) ? why[step][sub] : undefined;
    const text = isRecord(leaf) ? leaf["reasoning"] : undefined;
    return typeof text === "string" && text.trim() !== "" ? text : null;
  };
  const groups: StepGroup[] = [];
  for (const step of Object.keys(body).sort(stepOrder)) {
    const value = body[step];
    const subSteps: SubStep[] = isRecord(value)
      ? Object.keys(value)
          .sort(stepOrder)
          .flatMap((id) =>
            typeof value[id] === "boolean"
              ? [
                  {
                    id,
                    passed: value[id] as boolean,
                    reasoning: reasoningOf(step, id),
                  },
                ]
              : [],
          )
      : typeof value === "boolean"
        ? [{ id: step, passed: value, reasoning: reasoningOf(step, step) }]
        : [];
    if (subSteps.length > 0) {
      groups.push({ step, subSteps });
    }
  }
  return groups.length === 0
    ? null
    : {
        groups,
        hasReasoning: groups.some((g) =>
          g.subSteps.some((s) => s.reasoning !== null),
        ),
      };
}

/** Display name of a track id: `eating_disorders` -> `Eating Disorders`. */
export function trackDisplayName(track: string): string {
  return track
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/** The weighted steps of a track; with the answers, only those the classifier could answer. */
function weightedSteps(
  track: string,
  weights: MainConcernWeights,
  answers: MainConcernAnswers | undefined,
): string[] {
  return Object.keys(weights).filter(
    (step) =>
      weights[step]?.[track] !== undefined &&
      (answers === undefined || step in answers),
  );
}

function trackSteps(
  track: string,
  weights: MainConcernWeights | null,
  texts: StepTexts | null,
  answers: MainConcernAnswers | undefined,
  stateOf: (step: string) => StepState,
): TrackStep[] {
  if (weights === null) {
    return [];
  }
  return weightedSteps(track, weights, answers)
    .sort(stepOrder)
    .map((step) => ({
      step,
      text: texts?.[step] ?? null,
      state: stateOf(step),
      points: weights[step]?.[track] ?? null,
    }));
}

/** The attainable total over the steps the classifier answered; null until its answers are read. */
function trackMax(
  track: string,
  weights: MainConcernWeights | null,
  answers: MainConcernAnswers | undefined,
): number | null {
  if (weights === null || answers === undefined) {
    return null;
  }
  return weightedSteps(track, weights, answers).reduce((sum, step) => {
    const weight = weights[step]?.[track] ?? 0;
    return weight > 0 ? sum + weight : sum;
  }, 0);
}

function buildRound(
  routing: RoutingEvent,
  round: number,
  alternatives: readonly WhyNotEvent[],
  classification: ClassificationEvent | null,
  turnNumber: number | null,
  input: ClassificationStateInput,
): MainConcernRound {
  const documents =
    routing.detail.governingConfig === null
      ? undefined
      : input.documents?.get(routing.detail.governingConfig.id);
  const weights = documents?.weights ?? null;
  const texts = documents?.stepTexts ?? null;
  const classifierId = routing.detail.decisionId.slice(
    0,
    -NEXT_TRACK_SUFFIX.length,
  );
  const answers =
    classification === null
      ? undefined
      : input.answers?.get(classification.eventRef);

  const selectedScore =
    answers !== undefined && weights !== null
      ? Object.keys(weights).reduce(
          (sum, step) =>
            answers[step] === true
              ? sum + (weights[step]?.[routing.detail.selectedChoice] ?? 0)
              : sum,
          0,
        )
      : null;

  const selected: TrackScore = {
    track: routing.detail.selectedChoice,
    selected: true,
    score: selectedScore,
    max: trackMax(routing.detail.selectedChoice, weights, answers),
    steps: trackSteps(
      routing.detail.selectedChoice,
      weights,
      texts,
      answers,
      (step) =>
        answers === undefined
          ? "unknown"
          : answers[step] === true
            ? "yes"
            : "no",
    ),
  };

  const others = alternatives.map((alt): TrackScore => {
    const unmet = new Set(alt.detail.unmetCriteria);
    return {
      track: alt.detail.alternative,
      selected: false,
      score: alt.detail.comparedScore?.value ?? null,
      max: trackMax(alt.detail.alternative, weights, answers),
      // Unmet is the producer's "no"; absence from it is not a yes (the step may be
      // outside the classifier's schema), so only the answers can say yes.
      steps: trackSteps(
        alt.detail.alternative,
        weights,
        texts,
        answers,
        (step) =>
          answers !== undefined
            ? answers[step] === true
              ? "yes"
              : "no"
            : unmet.has(`${classifierId}.${step}`)
              ? "no"
              : "unknown",
      ),
    };
  });

  // The winner outranks every alternative, so an unread score must not sink it.
  const rank = (t: TrackScore) =>
    t.score ?? (t.selected ? Infinity : -Infinity);
  const tracks = [selected, ...others].sort(
    (a, b) => rank(b) - rank(a) || Number(b.selected) - Number(a.selected),
  );
  return {
    round,
    turnNumber,
    decisionEventRef: routing.eventRef,
    selectedTrack: routing.detail.selectedChoice,
    tracks,
    classificationEventRef: classification?.eventRef ?? null,
    weightsDocument: routing.detail.governingConfig,
  };
}

function classifierOf(criterion: CriterionEvent): string {
  const id = criterion.detail.criterionId;
  const dot = id.indexOf(".");
  return dot < 0 ? id : id.slice(0, dot);
}

/**
 * Accumulates the run's events up to `throughTurn`: the latest flow position,
 * every main-concern round in order, and the latest finished evaluation per
 * classifier (a later turn's re-evaluation replaces the earlier one).
 */
export function buildClassificationState(
  input: ClassificationStateInput,
): ClassificationState {
  const { lab, throughTurn } = input;
  const turns = lab.turns.filter(
    (turn) => throughTurn === null || turn.turnNumber <= throughTurn,
  );
  // The turn the Lab grouped each event under: an event may carry no turn number of its own.
  const turnOf = (event: TraceEvent): number | null =>
    turnByEvent.get(event) ?? null;
  const turnByEvent = new Map<TraceEvent, number>(
    turns.flatMap((turn) =>
      turn.events.map((event): [TraceEvent, number] => [
        event,
        turn.turnNumber,
      ]),
    ),
  );
  const events: TraceEvent[] = [
    ...(throughTurn === null || turns.length > 0 ? lab.preTurnEvents : []),
    ...turns.flatMap((turn) => turn.events),
  ].sort((a, b) => a.sequence - b.sequence);

  const lastTurn = turns[turns.length - 1];
  const position = lastTurn?.after ?? lastTurn?.before ?? null;

  const mainConcernIds = new Set<string>();
  const rounds: MainConcernRound[] = [];
  const routings = events.filter(
    (e): e is RoutingEvent =>
      e.family === "routing" && e.detail.decisionId.endsWith(NEXT_TRACK_SUFFIX),
  );
  const classifications = events.filter(
    (e): e is ClassificationEvent => e.family === "classification",
  );
  for (const routing of routings) {
    const classifierId = routing.detail.decisionId.slice(
      0,
      -NEXT_TRACK_SUFFIX.length,
    );
    mainConcernIds.add(classifierId);
    // Only a classification the decision cites informed it: a pinned slot cites none.
    const cited =
      classifications.find((c) =>
        routing.detail.evidenceRefs.includes(c.eventRef),
      ) ?? null;
    const alternatives = events.filter(
      (e): e is WhyNotEvent =>
        e.family === "why_not" &&
        e.detail.decisionEventRef === routing.eventRef,
    );
    rounds.push(
      buildRound(
        routing,
        rounds.length + 1,
        alternatives,
        cited,
        turnOf(routing),
        input,
      ),
    );
  }

  const drafts = new Map<
    string,
    {
      turnNumber: number | null;
      setAside: boolean;
      criteria: EvaluationCriterion[];
      passed: boolean | null;
      classificationEventRef: string | null;
    }
  >();
  const draftOf = (classifierId: string, turnNumber: number | null) => {
    const held = drafts.get(classifierId);
    if (held !== undefined && held.turnNumber === turnNumber) {
      return held;
    }
    const fresh = {
      turnNumber,
      setAside: false,
      criteria: [],
      passed: null,
      classificationEventRef: null,
    };
    drafts.set(classifierId, fresh);
    return fresh;
  };

  for (const event of events) {
    if (event.family === "classification") {
      if (mainConcernIds.has(event.detail.classifierId)) {
        continue;
      }
      const draft = draftOf(event.detail.classifierId, turnOf(event));
      draft.setAside = event.status === "no_action";
      draft.classificationEventRef = event.eventRef;
    } else if (event.family === "criterion_threshold") {
      const classifierId = classifierOf(event);
      if (mainConcernIds.has(classifierId)) {
        continue;
      }
      const draft = draftOf(classifierId, turnOf(event));
      if (event.detail.criterionId === `${classifierId}.${VERDICT_CRITERION}`) {
        draft.passed = event.detail.passed;
      } else {
        draft.criteria.push({
          criterionId: event.detail.criterionId,
          label: event.label,
          passed: event.detail.passed,
        });
      }
    }
  }
  const evaluations = [...drafts.entries()].map(
    ([classifierId, draft]): FinishedEvaluation => ({
      classifierId,
      classificationEventRef: draft.classificationEventRef,
      reading:
        draft.classificationEventRef === null
          ? null
          : (input.readings?.get(draft.classificationEventRef) ?? null),
      turnNumber: draft.turnNumber,
      passed: draft.passed,
      setAside: draft.setAside,
      criteria: draft.criteria,
    }),
  );

  return {
    position,
    rounds,
    evaluations,
    flow: buildFlow(
      rounds,
      evaluations,
      mainConcernIds,
      new Set(
        classifications
          .filter((c) => !mainConcernIds.has(c.detail.classifierId))
          .flatMap((c) =>
            c.coordinate.track === null ? [] : [c.coordinate.track],
          ),
      ),
      position,
    ),
    empty: rounds.length === 0 && evaluations.length === 0,
  };
}

const STAGE_ID = /_(screening|full_evaluation)$/;

const spaced = (id: string) => id.replaceAll("_", " ");

/**
 * The route as far as the events show it: main concern, every evaluation that
 * ran, the stage the conversation is in when it has not been classified yet,
 * and a selected track whose first stage has not started. The stages a track
 * runs come from the pack's flow, not from its name, so none is guessed ahead.
 */
function buildFlow(
  rounds: readonly MainConcernRound[],
  evaluations: readonly FinishedEvaluation[],
  mainConcernIds: ReadonlySet<string>,
  startedTracks: ReadonlySet<string>,
  position: RuntimePosition | null,
): FlowStep[] {
  if (rounds.length === 0) {
    return [];
  }
  const ids = [...mainConcernIds, ...evaluations.map((e) => e.classifierId)];
  const agent = position?.agent ?? null;
  if (agent !== null && !ids.includes(agent) && STAGE_ID.test(agent)) {
    ids.push(agent);
  }
  const steps = ids.map((id): FlowStep => ({
    id,
    label: spaced(id),
    status: agent === id ? "current" : "done",
  }));
  const waiting = [...new Set(rounds.map((r) => r.selectedTrack))].filter(
    (track) => !startedTracks.has(track),
  );
  return [
    ...steps,
    ...waiting.map((track): FlowStep => ({
      id: `track:${track}`,
      label: `${spaced(track)} track`,
      status: "next",
    })),
  ];
}

/**
 * The frozen text of the archive file a config id names (`<lang>/<phase>/.../x.yaml`):
 * `undefined` when the candidate has no such file, `null` when it has one whose
 * text the service withheld (too large to inline).
 */
export function frozenFileText(
  families: readonly {
    files: readonly { path: string; text?: string | null }[];
  }[],
  configId: string,
): string | null | undefined {
  for (const family of families) {
    for (const file of family.files) {
      if (file.path === configId || file.path.endsWith(`/${configId}`)) {
        return file.text ?? null;
      }
    }
  }
  return undefined;
}

/** The statements file that sits beside a `<agent>_diagnosis.yaml` weight document. */
export function classificationDocumentId(weightsDocumentId: string): string {
  return weightsDocumentId.replace(/_diagnosis\.yaml$/, "_classification.txt");
}

/**
 * The candidate a run was dispatched on. The run exposes no candidate id, only
 * the revision number the service resolved and the content hash it attests, so
 * the id is found in the listing and must match BOTH; anything else is null.
 */
export function runCandidateOf(
  candidates: readonly CandidateSummary[],
  revisionNumber: number | null,
  runHash: string | null,
): CandidateSummary | null {
  if (revisionNumber === null || !isAttested(runHash)) {
    return null;
  }
  return (
    candidates.find(
      (candidate) =>
        candidate.revision?.number === revisionNumber &&
        candidate.contentHash === runHash,
    ) ?? null
  );
}
